/**
 * Pulls a user-readable error message out of whatever the API client tossed
 * into a catch block. `InterceptedFetch` rejects with the raw text body for
 * `text/plain` responses, the parsed JSON body for `application/json`, or a
 * `Response` object/`Error` otherwise — this normalizes all of those down to
 * a single string we can surface in a toast.
 *
 * Returns `fallback` when the error carries no useful message.
 */
export const extractErrorMessage = (err: unknown, fallback: string): string => {
    if (err == null) return fallback;

    if (typeof err === "string") {
        const trimmed = err.trim();
        return trimmed || fallback;
    }

    if (err instanceof Error) {
        return err.message?.trim() || fallback;
    }

    if (typeof err === "object") {
        const e = err as Record<string, any>;
        if (typeof e.message === "string" && e.message.trim()) return e.message.trim();
        if (typeof e.error === "string" && e.error.trim()) return e.error.trim();
        if (typeof e.detail === "string" && e.detail.trim()) return e.detail.trim();
        if (typeof e.statusText === "string" && e.statusText.trim()) return e.statusText.trim();
    }

    return fallback;
};
